import { forwardRef, useId } from "react";
import classNames from "classnames";
import Select from "react-select";
import { InfoTooltip } from "../Tooltip";
import type { ComponentProps, ReactNode } from "react";
import type { GroupBase, Props } from "react-select";

export function Label({ className, ...labelProps }: ComponentProps<"label">) {
  return (
    // eslint-disable-next-line jsx-a11y/label-has-associated-control
    <label
      {...labelProps}
      className={classNames(
        "block text-sm font-medium text-gray-800",
        className,
      )}
    />
  );
}

type InputProps =
  | ({ type: "textarea" } & ComponentProps<"textarea">)
  | ComponentProps<"input">;

export const Input = forwardRef<HTMLInputElement, InputProps>(
  function Input(props, ref) {
    const className = classNames("text-input", props.className);

    if (props.type === "textarea") {
      return (
        <textarea
          {...(props as ComponentProps<"textarea">)}
          className={className}
        />
      );
    }

    return (
      <input
        {...(props as ComponentProps<"input">)}
        className={className}
        ref={ref}
      />
    );
  },
);

type InputErrorProps = {
  id: string;
  children?: ReactNode | null;
};

export function InputError({ children, id }: InputErrorProps) {
  if (!children) {
    return null;
  }

  return (
    <div role="alert" id={id} className="text-sm text-red-600">
      {children}
    </div>
  );
}

export const Field = forwardRef<
  HTMLInputElement,
  {
    defaultValue?: string | null;
    name: string;
    label?: ReactNode;
    description?: ReactNode;
    info?: ReactNode;
    className?: string;
    error?: string | null;
  } & InputProps
>(function Field(
  {
    defaultValue,
    error,
    name,
    label,
    description,
    info,
    className,
    id,
    ...props
  },
  ref,
) {
  const prefix = useId();
  const inputId = id ?? `${prefix}-${name}`;
  const errorId = `${inputId}-error`;

  return (
    <div className={classNames("space-y-2", className)}>
      {label && (
        <Label className="flex items-center gap-2" htmlFor={inputId}>
          {label}
          {info && <InfoTooltip>{info}</InfoTooltip>}
        </Label>
      )}

      {description && (
        <span className="text-sm text-gray-500">{description}</span>
      )}

      <Input
        // @ts-expect-error no idea 🤷‍♂️
        ref={ref}
        {...(props as InputProps)}
        name={name}
        id={inputId}
        defaultValue={defaultValue}
        aria-describedby={error ? errorId : undefined}
        error={error}
        className="disabled:cursor-not-allowed disabled:bg-gray-200"
      />

      <InputError id={errorId}>{error}</InputError>
    </div>
  );
});

export function SelectField<
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({
  id,
  name,
  className,
  error,
  label,
  info,
  ...props
}: Props<Option, IsMulti, Group> & {
  label: string;
  error?: string | null;
  info?: string;
}) {
  const prefix = useId();
  const inputId = id ?? `${prefix}-${name}`;
  const errorId = `${inputId}-error`;

  return (
    <div className={classNames("space-y-2", className)}>
      <Label htmlFor={inputId} className="flex items-center gap-2">
        {label}
        {info && <InfoTooltip>{info}</InfoTooltip>}
      </Label>

      <Select
        inputId={inputId}
        instanceId={inputId}
        name={name}
        className="select"
        classNamePrefix="custom-select"
        menuPlacement="auto"
        closeMenuOnSelect={props.closeMenuOnSelect ?? !props.isMulti}
        {...props}
      />
      <InputError id={errorId}>{error}</InputError>
    </div>
  );
}

export function CheckboxField({
  className,
  label,
  options,
  error,
  onChange,
  description,
}: {
  label?: string;
  className?: string;
  description?: ReactNode;
  options: {
    name: string;
    label: string;
    checked: boolean | undefined;
  }[];
  error?: string;
  id?: string;
  onChange?: (value: string) => void;
}) {
  const prefix = useId();
  const errorId = `${prefix}-error`;

  return (
    <div className={className}>
      {label && <Label>{label}</Label>}
      {description && (
        <span className="text-sm text-gray-500">{description}</span>
      )}

      <fieldset className={classNames({ "mt-4": label })}>
        <legend className="sr-only">{label}</legend>

        <div className="space-y-4">
          {options.map((option) => (
            <div key={option.name} className="flex">
              <input
                className="mt-1 h-4 w-4 border-gray-300 text-primary-600 focus:ring-primary-500"
                type="checkbox"
                id={option.name}
                name={option.name}
                onChange={(event) => onChange && onChange(event.target.value)}
                aria-describedby={error ? errorId : undefined}
                defaultChecked={option.checked}
              />
              <Label htmlFor={option.name} className="ml-3">
                {option.label}
              </Label>
            </div>
          ))}
        </div>
      </fieldset>

      <InputError id={errorId}>{error}</InputError>
    </div>
  );
}

interface RadioOption {
  value: string;
  label: string;
}

interface RadioGroupProps {
  name: string;
  label?: string;
  className?: string;
  options: RadioOption[];
  error?: string;
  value?: string;
  info?: string;
  align?: "vertical" | "horizontal"; // New prop
  onChange?: (value: string) => void;
}

export function RadioGroup({
  name,
  label,
  className,
  value,
  options,
  error,
  info,
  align = "vertical", // Default alignment
  onChange,
}: RadioGroupProps) {
  const prefix = useId();
  const errorId = `${prefix}-error`;

  return (
    <div className={className}>
      {label && (
        <Label className="mb-2 flex items-center gap-2" htmlFor={name}>
          {label}
          {info && <InfoTooltip>{info}</InfoTooltip>}
        </Label>
      )}

      <fieldset className={classNames({ "mt-4": label })}>
        <legend className="sr-only">{label}</legend>

        <div
          className={classNames({
            "space-y-4": align === "vertical",
            "flex space-x-4": align === "horizontal", // Horizontal alignment
          })}
        >
          {options.map((option) => (
            <div key={option.value} className="flex items-center">
              <input
                className="h-4 w-4 border-gray-300 text-primary-600 focus:ring-primary-500"
                type="radio"
                id={`${name}-${option.value}`}
                name={name}
                value={option.value}
                onChange={(event) => onChange && onChange(event.target.value)}
                aria-describedby={error ? errorId : undefined}
                defaultChecked={value === option.value}
              />
              <Label htmlFor={`${name}-${option.value}`} className="ml-3">
                {option.label}
              </Label>
            </div>
          ))}
        </div>
      </fieldset>

      <InputError id={errorId}>{error}</InputError>
    </div>
  );
}
